/**
 * Local-only fetch patch that returns deterministic JSON for
 * `https://api.gisperg.wine/api/*` calls when the storefront is running on
 * localhost. Prod builds and every other environment fall through to the
 * real `window.fetch`, so this file is invisible outside `npm run dev`.
 *
 * The mocks are intentionally minimal but shaped like the real payloads —
 * enough for every page to render without CORS errors, and enough to
 * exercise features like the prev/next arrows, package tiles, or the
 * Heurigen reservation flow.
 *
 * To extend: add another entry to the `MOCKS` map. The key is either an
 * exact pathname or a small regex; the value is either a plain object /
 * array returned as-is, or a function `(pathname, url) => data` for
 * things like `/api/Product/Headers/:id` that vary per-id.
 */

// ---------------------------------------------------------------------------
// Fixtures — thin, deterministic, but shaped like production.
// ---------------------------------------------------------------------------

const MOCK_PRICE = (countryId, value) => ({
    priceId: countryId,
    countryId,
    value,
    deviateTaxRate: null,
});

const MOCK_PRODUCT = (id, headerId, headerName, klassifikation, vintage, capacity, price, taxRate, category = 'Weiß') => ({
    productId: id,
    productHeaderId: headerId,
    header: {
        productHeaderId: headerId,
        name: headerName,
        klassifikation,
        declaration: '',
        taxRate,
    },
    caption: '',
    vintage: vintage ? `${vintage}-01-01T00:00:00Z` : null,
    alcohol: 12.5,
    capacity,
    takesSpace: 1,
    isInStock: true,
    isVisible: true,
    releaseDate: null,
    prices: [MOCK_PRICE(1, price), MOCK_PRICE(2, price + 2)],
});

const MOCK_HEADERS = [
    {
        productHeaderId: 1,
        kind: 'Product',
        name: 'Grüner Veltliner',
        klassifikation: 'Ried Holzspur',
        declaration: 'trocken',
        description: '<p>Ein klassischer Grüner mit lebendiger Frucht und feiner Würze.</p>',
        isVisible: true,
        showCaseImageLocation: 'GVL_0,75l_Gisperg',
        orderBy: 1,
        taxRate: 13,
        hasCategories: [
            { hasCategoryId: 1, categoryId: 1, category: { categoryId: 1, name: 'Grüner Veltliner', type: 'Klassifikation' } },
        ],
        products: [
            MOCK_PRODUCT(101, 1, 'Grüner Veltliner', 'Ried Holzspur', 2024, 0.75, 12.5, 13),
            MOCK_PRODUCT(102, 1, 'Grüner Veltliner', 'Ried Holzspur', 2024, 1.5, 26.0, 13),
            MOCK_PRODUCT(103, 1, 'Grüner Veltliner', 'Ried Holzspur', 2023, 0.375, 7.5, 13),
        ],
        linkedProducts: [],
    },
    {
        productHeaderId: 2,
        kind: 'Product',
        name: 'Klara',
        klassifikation: 'Gemischter Satz',
        declaration: 'trocken',
        description: '<p>Ein fröhlicher Sommerwein aus mehreren Rebsorten.</p>',
        isVisible: true,
        showCaseImageLocation: 'KLL_0,75l_Gisperg',
        orderBy: 2,
        taxRate: 13,
        hasCategories: [],
        products: [MOCK_PRODUCT(201, 2, 'Klara', 'Gemischter Satz', 2025, 0.75, 8.2, 13)],
        linkedProducts: [],
    },
    {
        productHeaderId: 3,
        kind: 'Product',
        name: 'Pinot Noir',
        klassifikation: 'Thermenregion DAC',
        declaration: 'trocken',
        description: '<p>Eleganter Pinot mit feinem Tannin.</p>',
        isVisible: true,
        showCaseImageLocation: 'PNR_0,75l_Gisperg',
        orderBy: 3,
        taxRate: 13,
        hasCategories: [],
        products: [MOCK_PRODUCT(301, 3, 'Pinot Noir', 'Thermenregion DAC', 2023, 0.75, 14.5, 13)],
        linkedProducts: [],
    },
    {
        productHeaderId: 55,
        kind: 'Package',
        name: 'Schwammerl Paket',
        klassifikation: 'Dieses Paket enthält 6 Weißweine',
        declaration: '',
        description: '<p>Sechs perfekte Begleiter zu Pilzgerichten aller Art.</p>',
        isVisible: true,
        showCaseImageLocation: 'LAS_Paket_Gisperg',
        orderBy: 99,
        taxRate: 13,
        hasCategories: [],
        products: [],
        linkedProducts: [],
        productQuantities: [
            {
                productQuantityId: 1,
                packageId: 55,
                productId: 101,
                quantity: 3,
                orderBy: 0,
                note: 'zu Steinpilzen',
                product: MOCK_PRODUCT(101, 1, 'Grüner Veltliner', 'Ried Holzspur', 2024, 0.75, 12.5, 13),
            },
            {
                productQuantityId: 2,
                packageId: 55,
                productId: 201,
                quantity: 3,
                orderBy: 1,
                note: 'zu Champignons',
                product: MOCK_PRODUCT(201, 2, 'Klara', 'Gemischter Satz', 2025, 0.75, 8.2, 13),
            },
        ],
    },
];

const MOCK_CATEGORIES = [
    { categoryId: 1, name: 'Grüner Veltliner', type: 'Klassifikation' },
    { categoryId: 2, name: 'Pinot Noir', type: 'Klassifikation' },
    { categoryId: 3, name: 'trocken', type: 'Geschmack' },
    { categoryId: 4, name: 'Wein', type: 'Typ' },
    { categoryId: 5, name: 'Paket', type: 'Typ' },
];

const MOCK_HEURIGEN = {
    heurigenConfigId: 1,
    title: 'Heurigen genießen',
    annotation: '#1000 Geschmäcker bei einem Besuch',
    description: '<p><strong>09.-12. JULI</strong> | Ob Sonnenanbeter oder Schattenfreund - in unserem Innenhof findet auch im Sommer jeder den richtigen Platz.</p><p><strong>HEURIGEN-GOODIE:</strong> Weinverkauf auch abends und am Wochenende</p><p><strong>TISCHRESERVIERUNG EMPFOHLEN:</strong> Reservieren Sie direkt hier.</p>',
    imageLocation: '',
    foodMenuImageLocation: '',
    foodMenuContent: '',
    reservationActive: true,
    reservationTitle: 'Reservierung Juli',
    reservationSubtitle: 'Öffnungszeiten: 12 - 22 Uhr | Küche bis 21.30 Uhr',
    reservationMinDateTime: '2026-07-09T12:00:00Z',
    reservationMaxDateTime: '2026-07-12T22:00:00Z',
    sitzplatzActive: true,
};

const MOCK_WEINERLEBEN = {
    weinErlebenConfigId: 1,
    title: 'Wein erleben',
    annotation: '#1000 Eindrücke in einem Fest',
    description: '<p>Gönnen Sie sich eine Pause vom Alltagsstress, nehmen Sie sich Zeit und erleben Sie gemeinsam mit uns 1000 Momente in einem Glas. Gehen Sie auf große Entdeckungsreise im kleinen Weinglas und genießen Sie unsere Weine dort, wo sie entstehen.</p>',
    imageLocation: '/images/weinerleben_black.webp',
    goodiesTitle: '#WEIN ERLEBEN GOODIES:',
    goodiesContent: '<p>Weinkauf bis 20 Uhr. Für kürzere Wartezeit vorab Wein zum Mitnehmen bestellen. Unser Tipp: Click &amp; Collect im <a href="/weinkauf/shop/">Onlineshop</a></p>'
        + '<p>14. November 2026<br>15 - 21 Uhr<br>Einlass:<br>15 - 19 Uhr</p>'
        + '<p>Anmeldung:<br><a href="tel:+43225381464">+43 2253/81464</a><br><a href="mailto:wein@weingut-gisperg.at">wein@weingut-gisperg.at</a><br>Preis: 39€</p>'
        + '<p>Weinverkauf:<br>08 - 12 | 13 - 20 Uhr<br>Samstags geöffnet</p>'
        + '<p>Übernachtung:<br>Wir empfehlen Gästezimmer<br>Frühwirth vis a vis vom Weingut.<br><a href="/herkunft/tourismus/">zum Tourismus</a></p>',
    facts: [
        'Sektempfang', 'Wein & Käse Genussparcours', 'Mehrere Weinbars im Keller',
        'Reichhaltiges Buffet (auch vegetarisch/vegan)', 'Naturreiner Traubensaft', 'Eröffnung Geschenkeshop',
        'Samstag geöffnet', 'Weinverkauf bis 20 Uhr', 'Click & Collect', 'Verkosten & liefern lassen',
    ],
    sliderImages: [1, 2, 3, 4, 5, 6].map((n) => `/images/weinerleben/weinerleben_${n}.webp`),
    reservationActive: true,
    reservationTitle: 'Anmeldung Wein Erleben',
    reservationSubtitle: 'Einlass 15 – 19 Uhr',
    reservationMinDateTime: '2026-11-14T15:00:00',
    reservationMaxDateTime: '2026-11-14T19:00:00',
};

// ---------------------------------------------------------------------------
// Mock router — pathname → data (or a factory).
// ---------------------------------------------------------------------------

const MOCKS = [
    { match: /^\/api\/Product\/Headers$/, data: () => MOCK_HEADERS },
    {
        match: /^\/api\/Product\/Headers\/(\d+)$/,
        data: (path) => {
            const id = Number(path.match(/\/(\d+)$/)?.[1] ?? 0);
            return MOCK_HEADERS.find((h) => h.productHeaderId === id) ?? null;
        },
    },
    {
        match: /^\/api\/Product\/(\d+)$/,
        data: (path) => {
            const id = Number(path.match(/\/(\d+)$/)?.[1] ?? 0);
            for (const h of MOCK_HEADERS) {
                const p = (h.products ?? []).find((pp) => pp.productId === id);
                if (p) return p;
            }
            return null;
        },
    },
    { match: /^\/api\/Category\/All$/, data: () => MOCK_CATEGORIES },
    { match: /^\/api\/Heurigen$/, data: () => MOCK_HEURIGEN },
    { match: /^\/api\/WeinErleben$/, data: () => MOCK_WEINERLEBEN },
    // Termine calendar: no upcoming events locally (avoids the dev overlay).
    { match: /^\/api\/Event\/All$/, data: () => [] },
    // Delivery check: return true when quantity is a positive multiple of 6.
    {
        match: /^\/api\/Delivery\/ValidDelivery$/,
        data: (_path, url) => {
            const q = Number(new URL(url).searchParams.get('quantity') || 0);
            return q > 0 && q % 6 === 0;
        },
    },
    // Reservation / newsletter / contact POSTs: no side-effect, always ok.
    { match: /^\/api\/Reservation\/New$/, data: () => ({ ok: true }) },
];

function matchMock(pathname) {
    for (const rule of MOCKS) {
        const m = pathname.match(rule.match);
        if (m) return rule;
    }
    return null;
}

// ---------------------------------------------------------------------------
// Install the patch. Idempotent — safe to call multiple times.
// ---------------------------------------------------------------------------

export function installLocalMocks() {
    if (typeof window === 'undefined') return;
    // Only run on localhost. Prod domain uses the real API directly.
    const host = window.location.hostname;
    if (host !== 'localhost' && host !== '127.0.0.1') return;
    if (window.__gispergMockInstalled__) return;
    window.__gispergMockInstalled__ = true;

    const originalFetch = window.fetch.bind(window);

    window.fetch = (input, init) => {
        try {
            const url = typeof input === 'string' ? input : input?.url;
            if (typeof url === 'string' && url.includes('api.gisperg.wine')) {
                const parsed = new URL(url);
                const rule = matchMock(parsed.pathname);
                if (rule) {
                    const value = typeof rule.data === 'function'
                        ? rule.data(parsed.pathname, url)
                        : rule.data;
                    const body = value == null
                        ? ''
                        : (typeof value === 'string' ? value : JSON.stringify(value));
                    const status = value == null ? 404 : 200;
                    // eslint-disable-next-line no-console
                    console.debug(`[mockApi] ${parsed.pathname} → ${status}`);
                    return Promise.resolve(new Response(body, {
                        status,
                        headers: { 'content-type': 'application/json' },
                    }));
                }
            }
        } catch (_) { /* fall through to the real fetch */ }
        return originalFetch(input, init);
    };
}
